import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";

export default function About() {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 pb-20 pt-32 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-6 text-5xl font-bold">
            {t("about.hero.title", "Design Your Dream Kitchen")}
          </h1>
          <p className="mx-auto max-w-3xl text-xl text-primary-100">
            {t(
              "about.hero.subtitle",
              "BYK combines the timeless Raumplan philosophy of Adolf Loos with cutting-edge AI technology to revolutionize kitchen design in Israel."
            )}
          </p>
        </div>
      </section>

      {/* Raumplan Philosophy */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-6 text-center text-3xl font-bold text-gray-900">
              {t("about.raumplan.title", "The Raumplan Philosophy")}
            </h2>
            <div className="prose prose-lg mx-auto text-gray-600">
              <p>
                {t(
                  "about.raumplan.p1",
                  "Raumplan, or 'space plan', was developed by Austrian architect Adolf Loos in the early 20th century. Unlike traditional floor plan design that treats each level as a flat plane, Raumplan considers the three-dimensional volume of each space, allowing rooms to flow into one another at different heights."
                )}
              </p>
              <p>
                {t(
                  "about.raumplan.p2",
                  "In kitchen design, this approach means thinking beyond the countertop level. We consider vertical storage, overhead clearances, sightlines, and the volumetric relationship between cooking zones, storage areas, and social spaces."
                )}
              </p>
              <p>
                {t(
                  "about.raumplan.p3",
                  "BYK brings this philosophy into the digital age, using 3D visualization and AI-powered layout suggestions to create kitchens that are not just functional, but spatially harmonious."
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
            {t("about.features.title", "Why Choose BYK?")}
          </h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Feature 1 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.ai.title", "AI-Powered Design")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.ai.description",
                  "Our Gemini 3 Pro AI generates photorealistic 4K renders and intelligent layout suggestions based on your requirements."
                )}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.catalog.title", "Premium Catalog")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.catalog.description",
                  "Access products from Niga, Teka, Neolith, and other leading Israeli and international manufacturers."
                )}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.rules.title", "Smart Rules")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.rules.description",
                  "Built-in Kashrut compliance, work triangle optimization, and accessibility standards ensure your kitchen meets all requirements."
                )}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.3d.title", "Real-time 3D")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.3d.description",
                  "WebGPU-powered Babylon.js rendering lets you explore your kitchen design in immersive 3D before making any commitments."
                )}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.bom.title", "Bill of Materials")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.bom.description",
                  "Generate professional BOMs with exact product specifications, quantities, and pricing for your contractors."
                )}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="rounded-xl bg-white p-8 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {t("about.features.edge.title", "Edge Computing")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "about.features.edge.description",
                  "Built on Cloudflare's global edge network for lightning-fast performance anywhere in Israel and beyond."
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            {t("about.partners.title", "Our Manufacturing Partners")}
          </h2>
          <p className="mb-12 text-gray-600">
            {t(
              "about.partners.subtitle",
              "We work with Israel's leading kitchen product manufacturers"
            )}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-16">
            <div className="text-center">
              <div className="mb-2 text-4xl font-bold text-gray-400">Niga</div>
              <p className="text-sm text-gray-500">Sinks & Taps</p>
            </div>
            <div className="text-center">
              <div className="mb-2 text-4xl font-bold text-gray-400">Teka</div>
              <p className="text-sm text-gray-500">Ovens & Cooktops</p>
            </div>
            <div className="text-center">
              <div className="mb-2 text-4xl font-bold text-gray-400">Neolith</div>
              <p className="text-sm text-gray-500">Sintered Surfaces</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold">
            {t("about.cta.title", "Ready to Design Your Kitchen?")}
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-primary-100">
            {t(
              "about.cta.subtitle",
              "Start your free design session today and experience the future of kitchen planning."
            )}
          </p>
          <Link
            to="/login"
            className="inline-block rounded-full bg-white px-8 py-4 text-lg font-semibold text-primary-600 transition-colors hover:bg-primary-50"
          >
            {t("about.cta.button", "Get Started Free")}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2025 BYK - Build Your Kitchen. All rights reserved.</p>
      </footer>
    </div>
  );
}
